import { useCallback, useEffect, useRef, useState } from "react";
import { plannerAPI, scheduleAPI, tenantAPI } from "../../../api/client";
import { expandDayRanges } from "../../../utils/dateUtils";

const DEFAULT_ORG_WORKING_DAYS = ["MON", "TUE", "WED", "THU", "FRI"];

export function useRosterData(year, month) {
  const [teamData, setTeamData] = useState(null);
  const [shiftTemplates, setShiftTemplates] = useState([]);
  const [orgWorkingDays, setOrgWorkingDays] = useState(DEFAULT_ORG_WORKING_DAYS);
  const orgWorkingDaysRef = useRef(DEFAULT_ORG_WORKING_DAYS);
  const orgScheduleRef = useRef({ workingDays: DEFAULT_ORG_WORKING_DAYS, workStartTime: "09:00", workEndTime: "18:00" });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchData = useCallback(
    async (silent = false) => {
      if (!silent) setLoading(true);
      setError("");
      try {
        const [teamRes, tenantRes, orgRes] = await Promise.all([
          plannerAPI.getTeam({ year, month }),
          tenantAPI.getTenant(),
          scheduleAPI.getOrg(),
        ]);

        const teamRaw = teamRes.data;
        if (teamRaw?.users) {
          teamRaw.users = teamRaw.users.map((u) => ({
            ...u,
            days: expandDayRanges(u.dayRanges),
          }));
        }
        setTeamData(teamRaw);

        const days = Array.isArray(orgRes.data?.workingDays)
          ? orgRes.data.workingDays
          : DEFAULT_ORG_WORKING_DAYS;
        setOrgWorkingDays(days);
        orgWorkingDaysRef.current = days;
        orgScheduleRef.current = {
          workingDays: days,
          workStartTime: orgRes.data?.workStartTime || "09:00",
          workEndTime: orgRes.data?.workEndTime || "18:00",
        };

        const raw = tenantRes.data?.projectFormSchema?.shifts;
        const shifts = Array.isArray(raw)
          ? raw.map((s) => ({
              id: s.id,
              name: String(s.name || "").trim(),
              workStartTime: s.workStartTime || "09:00",
              workEndTime: s.workEndTime || "18:00",
            }))
          : [];
        setShiftTemplates(shifts);
      } catch (err) {
        setError(err?.response?.data?.message || "Failed to load roster");
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [year, month],
  );

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const applyShift = useCallback(async (userId, startDate, endDate, shift) => {
    await plannerAPI.updateUserSchedule(userId, {
      workingDays: orgWorkingDaysRef.current,
      workStartTime: shift.workStartTime,
      workEndTime: shift.workEndTime,
      effectiveFrom: startDate,
      effectiveTo: endDate || null,
    });
    // Clear any day overrides in the range so they don't mask the new shift
    const end = new Date(endDate || startDate);
    let d = new Date(startDate);
    while (d <= end) {
      const dateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      try { await plannerAPI.removeDayOverride(userId, dateStr); } catch {}
      d.setDate(d.getDate() + 1);
    }
  }, []);

  const applyOrgDefault = useCallback(async (userId, startDate, endDate, orgSchedule) => {
    const org = orgSchedule || {};
    await plannerAPI.updateUserSchedule(userId, {
      workingDays: Array.isArray(org.workingDays) ? org.workingDays : ["MON", "TUE", "WED", "THU", "FRI"],
      workStartTime: org.workStartTime || "09:00",
      workEndTime: org.workEndTime || "18:00",
      effectiveFrom: startDate,
      effectiveTo: endDate && endDate !== startDate ? endDate : null,
    });
  }, []);

  const clearShift = useCallback(async (userId, dateStr) => {
    const org = orgScheduleRef.current;
    const payload = {
      workingDays: org.workingDays,
      workStartTime: org.workStartTime,
      workEndTime: org.workEndTime,
      effectiveFrom: dateStr,
      isRestoreRow: true,
    };
    await plannerAPI.updateUserSchedule(userId, payload);
    try { await plannerAPI.removeDayOverride(userId, dateStr); } catch {}
  }, []);

  const applyDayOverride = useCallback(async (userId, dateStr, type) => {
    if (type === null) {
      await plannerAPI.removeDayOverride(userId, dateStr);
    } else {
      await plannerAPI.upsertDayOverride(userId, { date: dateStr, type });
    }
  }, []);

  const refresh = useCallback(() => fetchData(true), [fetchData]);

  return { teamData, shiftTemplates, orgWorkingDays, loading, error, applyShift, applyOrgDefault, applyDayOverride, clearShift, refresh };
}
